import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as actionTypes from '../constantes/actionTypes';
import { BackgroundImage, BiometricAndConsentText, BottomButton, BottomContainer, BottomText, ButtonsContainer, Component, MainText } from '../styles/onBoardStyle';
import { CliniqueState, Retailer, StepView } from '../type';

import { AnimatePresence } from 'framer-motion';
import SpringsMob from '../assets/Images/mobile/Springs.png';
import SpringsTab from '../assets/Images/tablet/Springs.png';
import { getMicrositePrivacyCheckboxCount, hasMicrositePrivacy } from '../constantes/micrositePrivacy';
import { sendAnalyticsPageView } from '../constantes/utilitaries';
import i18n from '../i18nextInit';
import ChinaPP from './customPrivacyPolicy/china';
import KoreanPP from './customPrivacyPolicy/korean';
import MicrositeApacPP from './customPrivacyPolicy/micrositeApac';
import MicrositeKoreanPP from './customPrivacyPolicy/micrositeKorean';
import MicrositeUsPP from './customPrivacyPolicy/micrositeUs';
import UsPP from './customPrivacyPolicy/us';
import ThPP from './customPrivacyPolicy/th';
import { trackPlausibleEvent } from '../services/analytics';

const OnBoard = () => {
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { market, sessionID, retailer, isMicrosite } = useSelector((state: CliniqueState) => state.session);
	const analyticsFlag = useRef<boolean>(false);

	// Uniquement pour la Korée
	const [isFirstChecked, checkFirst] = useState<boolean>(false);
	const [isScndChecked, checkscnd] = useState<boolean>(false);

	const checkBoxLanguages = ['KoKr', 'EnUs', 'CnCn'];
	const OnecheckBoxLanguages = ['ThTh'];
	const hasMicrositePrivacyPolicy = hasMicrositePrivacy(isMicrosite, i18n.language);
	const micrositeCheckboxCount = getMicrositePrivacyCheckboxCount(i18n.language);
	const shouldAutoScrollPrivacy = i18n.language !== 'CnCn';
	const scrollToBottom = () => {
		const bottomContainer = document.getElementById('BOTTOM_CONTAINER');
		if (!bottomContainer) {
			return;
		}
		window.scrollTo({ behavior: 'smooth', top: bottomContainer.offsetTop + 60 - window.innerHeight });
	};

	useEffect(() => {
		if (market && sessionID) {
			if (!analyticsFlag.current) {
				sendAnalyticsPageView(1, market, sessionID);
				analyticsFlag.current = true;
			}
		}
	}, [market, sessionID]);

	useEffect(() => {
		if (shouldAutoScrollPrivacy && ((hasMicrositePrivacyPolicy && micrositeCheckboxCount > 0) || checkBoxLanguages.includes(i18n.language) || OnecheckBoxLanguages.includes(i18n.language))) {
			if (isFirstChecked || isScndChecked) {
				setTimeout(() => {
					scrollToBottom();
				}, 0);
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasMicrositePrivacyPolicy, isFirstChecked, isScndChecked, micrositeCheckboxCount, shouldAutoScrollPrivacy]);

	const handleTerms = () => {
		let terms: string = '';
		if (retailer === Retailer.sephora) {
			// eslint-disable-next-line no-useless-concat
			terms = t('terms-1').replace(t('privacy-policy'), '<a href=' + 'https://www.sephora.fr/mentions-legales.html' + ' target="_blank"' + ' style="text-decoration: underline">' + t('privacy-policy') + '</a>');
		} else {
			// eslint-disable-next-line no-useless-concat
			terms = t('terms-1').replace(t('privacy-policy'), '<a href=' + t('privacy-policy-link') + ' target="_blank"' + ' style="text-decoration: underline">' + t('privacy-policy') + '</a>');
		}

		return terms;
	};

	const isButtonEnabled = () => {
		if (hasMicrositePrivacyPolicy) {
			if (micrositeCheckboxCount === 2) return isFirstChecked && isScndChecked;
			if (micrositeCheckboxCount === 1) return isFirstChecked;
			return true;
		}

		if (checkBoxLanguages.includes(i18n.language)) {
			if (shouldAutoScrollPrivacy && isFirstChecked && isScndChecked) {
				setTimeout(() => {
					window.scrollTo({ behavior: 'smooth', top: document.getElementById('BOTTOM_CONTAINER')!.offsetTop + 60 - window.innerHeight }); //300
				}, 300);
			}
			return isFirstChecked && isScndChecked;
		} else if (OnecheckBoxLanguages.includes(i18n.language)) {
			return isFirstChecked
		} else {
			return true;
		}
	};

	const handleAgree = () => {
		if (!isButtonEnabled()) return;

		trackPlausibleEvent('sd_intro_completed');
		trackPlausibleEvent('sd_terms_accepted');
		dispatch({ type: actionTypes.SWITCH_STEP, value: StepView.tuto });
	};

	return (
		<Component
			id='TEST'
			animate={{ x: 0 }}
			exit={{ x: window.RTL_LANGS.includes(i18n.language) || i18n.language === 'ArAr' ? '100%' : '-100%' }}
			onAnimationComplete={() => (document.getElementById('TEST')!.style.transform = 'initial')}
			transition={{ type: 'spring', duration: 0.5 }}>

			<BackgroundImage src={window.innerWidth > 768 ? SpringsTab : SpringsMob} />

			<AnimatePresence>
				{(hasMicrositePrivacyPolicy || t('biometric-data-notice').length > 0) && <BiometricAndConsentText key='ConsentText' initial={{ opacity: 0, height: 0, y: -20 }} animate={{ opacity: 1, height: 'auto', y: 0 }} exit={{ height: 0, opacity: 0 }}>
					{hasMicrositePrivacyPolicy ? t('microsite-privacy-title') : t('biometric-data-notice')}
				</BiometricAndConsentText>}

				{hasMicrositePrivacyPolicy && i18n.language === 'EnUs' && <MicrositeUsPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={checkFirst} checkScnd={checkscnd} />}

				{hasMicrositePrivacyPolicy && i18n.language === 'KoKr' && <MicrositeKoreanPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={checkFirst} checkScnd={checkscnd} />}

				{hasMicrositePrivacyPolicy && !['EnUs', 'KoKr'].includes(i18n.language) && <MicrositeApacPP isFirstChecked={isFirstChecked} checkFirst={checkFirst} requiresConsent={micrositeCheckboxCount === 1} />}

				{!hasMicrositePrivacyPolicy && i18n.language === 'KoKr' && <KoreanPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={checkFirst} checkScnd={checkscnd} />}

				{!hasMicrositePrivacyPolicy && i18n.language === 'EnUs' && <UsPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={checkFirst} checkScnd={checkscnd} />}

				{!hasMicrositePrivacyPolicy && i18n.language === 'CnCn' && <ChinaPP isFirstChecked={isFirstChecked} isScndChecked={isScndChecked} checkFirst={checkFirst} checkScnd={checkscnd} />}

				{!hasMicrositePrivacyPolicy && i18n.language === 'ThTh' && <ThPP isFirstChecked={isFirstChecked} checkFirst={checkFirst} />}

				{!hasMicrositePrivacyPolicy && !checkBoxLanguages.includes(i18n.language) && !OnecheckBoxLanguages.includes(i18n.language) && <MainText margintop={0} dangerouslySetInnerHTML={{ __html: handleTerms() }} key={0} initial={{ opacity: 0, height: 0, y: -20 }} animate={{ opacity: 1, height: 'auto', y: 0 }} exit={{ height: 0, opacity: 0 }}></MainText>}

				<BottomContainer key='BOTTOM_CONTAINER' id='BOTTOM_CONTAINER'>
					<ButtonsContainer>
						<BottomButton $isEnabled={isButtonEnabled()} whileTap={{ scale: 1.05 }} onClick={handleAgree}>
							<BottomText $isEnabled={isButtonEnabled()}>{hasMicrositePrivacyPolicy ? t('microsite-privacy-cta') : t('cta-OK')}</BottomText>
						</BottomButton>
					</ButtonsContainer>
				</BottomContainer>

			</AnimatePresence>
		</Component>
	);
};

const OnBoardComponent = () => {
	return <OnBoard />;
};

export default OnBoardComponent;
